"use client";

import { NOTICE, TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { CREDIT_AUTHOR_STATEMENT_GENERATOR_STRINGS } from "@/components/tools/credit-author-statement-generator";
import { t } from "@/lib/i18n/interpolate";
import {
  CREDIT_ROLE_IDS,
  type CreditContributor,
  type CreditDegree,
  type CreditRoleId,
} from "@/lib/tool-kit/publishing/credit";

/*
 * The authors × roles grid of the CRediT statement generator, and its role guide.
 * Layout only: every word comes from the widget's strings.
 */

type Strings = typeof CREDIT_AUTHOR_STATEMENT_GENERATOR_STRINGS;

export interface CreditRow extends CreditContributor {
  id: number;
}

/** "" is no contribution; "yes" a contribution with no degree stated. */
const DEGREE_VALUES = ["", "yes", "lead", "equal", "supporting"] as const;

export function setRole(
  row: CreditRow,
  role: CreditRoleId,
  degree: CreditDegree | ""
): CreditRow {
  const roles: CreditRow["roles"] = Object.fromEntries(
    Object.entries(row.roles).filter(([key]) => key !== role)
  );
  if (degree !== "") {
    roles[role] = degree;
  }
  return { ...row, roles };
}

function columnName(row: CreditRow, index: number, strings: Strings): string {
  return row.name.trim() || t(strings.authorLabel, { number: index + 1 });
}

function RoleCell({
  row,
  index,
  role,
  showDegrees,
  strings,
  onChange,
}: {
  row: CreditRow;
  index: number;
  role: CreditRoleId;
  showDegrees: boolean;
  strings: Strings;
  onChange: (degree: CreditDegree | "") => void;
}) {
  const label = t(strings.cellAria, {
    role: strings.roles[role],
    author: columnName(row, index, strings),
  });
  const value = row.roles[role] ?? "";
  if (!showDegrees) {
    return (
      <input
        aria-label={label}
        checked={value !== ""}
        className="size-4 accent-primary"
        onChange={(event) => onChange(event.target.checked ? "yes" : "")}
        type="checkbox"
      />
    );
  }
  const optionLabel = (degree: (typeof DEGREE_VALUES)[number]) => {
    if (degree === "") {
      return strings.degreeNone;
    }
    return degree === "yes" ? strings.degreeYes : strings.degreeLabels[degree];
  };
  return (
    <select
      aria-label={label}
      className="rounded-md border border-border bg-card px-1 py-1 text-foreground text-xs"
      onChange={(event) => onChange(event.target.value as CreditDegree | "")}
      value={value}
    >
      {DEGREE_VALUES.map((degree) => (
        <option key={degree} value={degree}>
          {optionLabel(degree)}
        </option>
      ))}
    </select>
  );
}

export function RoleMatrix({
  rows,
  showDegrees,
  strings,
  onChange,
}: {
  rows: readonly CreditRow[];
  showDegrees: boolean;
  strings: Strings;
  onChange: (id: number, role: CreditRoleId, degree: CreditDegree | "") => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {strings.matrixCaption}
        </caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.roleColumn}
            </th>
            {rows.map((row, index) => (
              <th className={`${TH} text-center`} key={row.id} scope="col">
                {columnName(row, index, strings)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CREDIT_ROLE_IDS.map((role) => (
            <tr key={role}>
              <th className={`${TD} font-medium`} scope="row">
                {strings.roles[role]}
              </th>
              {rows.map((row, index) => (
                <td className={`${TD} text-center`} key={row.id}>
                  <RoleCell
                    index={index}
                    onChange={(degree) => onChange(row.id, role, degree)}
                    role={role}
                    row={row}
                    showDegrees={showDegrees}
                    strings={strings}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RoleGuide({ strings }: { strings: Strings }) {
  return (
    <details className={NOTICE}>
      <summary className="cursor-pointer font-medium text-foreground">
        {strings.roleGuide}
      </summary>
      {/* A list, not a <dl>: the page's FAQ is the only description list it may hold. */}
      <ul className="mt-3 space-y-2">
        {CREDIT_ROLE_IDS.map((role) => (
          <li key={role}>
            <strong className="font-medium text-foreground">
              {strings.roles[role]}
            </strong>
            {strings.roleHintSeparator}
            {strings.roleHints[role]}
          </li>
        ))}
      </ul>
    </details>
  );
}
